import React, { useEffect, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Select, Spinner, TextInput } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  GetSupportDispatchOptions,
  GetSupportFormConfig,
  GetSupportWagePrefill,
  SubmitSupportTicket,
  SupportDispatchOption,
  SupportFormConfigResponse,
} from '../../services/dtos';
import {
  ATTACHMENT_ACCEPT,
  ComposerField,
  LanguageWarning,
  MisuseDisclaimer,
  TICKET_CATEGORIES,
  TicketCategoryMeta,
  attachmentPrecheck,
  categoryBySlug,
  checkLanguage,
  fmtBytes,
  fmtDate,
  uploadTicketAttachment,
} from './supportShared';
import { LanguageFilterHit } from '../../services/dtos';

// T4 — the category-driven composer (vision appendix forms A–G). /help/new
// is the picker; /help/new/:category renders that form. Client checks here
// are cosmetic: the server (TicketForms + the gates) re-validates everything.

const LABEL_CLS =
  'block mb-2 text-sm font-medium text-gray-900 dark:text-gray-400';
const INPUT_CLS =
  'block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500';

// sentinel for the "type it yourself" dropdown entry (A/C/D free-text hatch)
const OTHER = '__other';

const SupportNewTicketPage: React.FC = () => {
  const { category: slug } = useParams();
  if (!slug) return <CategoryPicker />;
  const meta = categoryBySlug(slug);
  if (!meta) return <Navigate to="/help/new" replace />;
  return <CategoryForm key={meta.slug} meta={meta} />;
};

// ── picker ──────────────────────────────────────────────────────────────────

const CategoryPicker: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<SupportFormConfigResponse | null>(null);

  useEffect(() => {
    apiServices.client
      .get(new GetSupportFormConfig())
      .then(setConfig)
      .catch(() => setConfig(null)); // banner is best-effort here
  }, []);

  return (
    <div className="mx-auto w-full flex max-w-3xl flex-col gap-6 dark:text-white">
      <div>
        <h2 className="text-2xl font-bold">New Support Request</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          What is this about? Each request type goes straight to the right
          person.
        </p>
      </div>

      {/* T7: an active ticket ban replaces the composer with the A-09
          message — the rest of the portal is untouched */}
      {config?.ticketBanned && (
        <Alert color="failure" icon={HiInformationCircle}>
          {config.bannedMessage}
        </Alert>
      )}
      {config && !config.ticketBanned && !config.supportOpen && (
        <Alert color="warning" icon={HiInformationCircle}>
          {config.closedMessage}
        </Alert>
      )}
      {config && !config.ticketBanned && config.supportOpen && !config.withinBusinessHours && (
        <AfterHoursBanner />
      )}

      <div className={config?.ticketBanned ? 'hidden' : 'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
        {TICKET_CATEGORIES.map((c) => (
          <Link
            key={c.slug}
            to={`/help/new/${c.slug}`}
            className="flex flex-col gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-sm"
          >
            <span className="font-semibold text-gray-900 dark:text-white">
              {c.title}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {c.blurb}
            </span>
            <span className="mt-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
              {c.destination}
            </span>
          </Link>
        ))}
      </div>

      <MisuseDisclaimer />

      <div>
        <Button color="gray" onClick={() => navigate('/help')}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const AfterHoursBanner: React.FC = () => (
  <Alert color="info" icon={HiInformationCircle}>
    The office is closed right now. You can still send your request — we'll
    pick it up next business day.
  </Alert>
);

// ── form ────────────────────────────────────────────────────────────────────

const CategoryForm: React.FC<{ meta: TicketCategoryMeta }> = ({ meta }) => {
  const navigate = useNavigate();
  const isA = meta.slug === 'pay';
  const isB = meta.slug === 'contract';
  const isC = meta.slug === 'call-info';
  const isD = meta.slug === 'organizing';
  const isE = meta.slug === 'political';
  const isF = meta.slug === 'membership';
  const isG = meta.slug === 'general';
  const usesShowDropdown = isA || isB || isC || isD || isG;

  const [config, setConfig] = useState<SupportFormConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dispatchOptions, setDispatchOptions] = useState<SupportDispatchOption[]>([]);

  // shared fields
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [relatedDispatch, setRelatedDispatch] = useState(''); // '', ident, or OTHER
  const [showFreeText, setShowFreeText] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [evidence, setEvidence] = useState<File | null>(null);
  // Form A
  const [workDate, setWorkDate] = useState('');
  const [position, setPosition] = useState('');
  const [positionDirty, setPositionDirty] = useState(false);
  const [expectedPay, setExpectedPay] = useState('');
  const [actualPay, setActualPay] = useState('');
  const [hoursWorked, setHoursWorked] = useState('');
  const [rateApplied, setRateApplied] = useState('');
  const [otPenaltyMissed, setOtPenaltyMissed] = useState('');
  const [callType, setCallType] = useState('');
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  // Form B
  const [urgent, setUrgent] = useState<'' | 'yes' | 'no'>('');
  const [contractUnderstanding, setContractUnderstanding] = useState('');
  // Form C
  const [callDate, setCallDate] = useState('');

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // T7: spans flagged by the pre-flight language check (inline reword UX)
  const [langHits, setLangHits] = useState<LanguageFilterHit[]>([]);

  useEffect(() => {
    apiServices.client
      .get(new GetSupportFormConfig())
      .then(setConfig)
      .catch(() => setConfig(null)) // fail open — the server still enforces
      .finally(() => setConfigLoading(false));
    if (usesShowDropdown)
      apiServices.client
        .get(new GetSupportDispatchOptions())
        .then(setDispatchOptions)
        .catch(() => setDispatchOptions([])); // dropdown degrades to free text
  }, [usesShowDropdown]);

  const selectedOption = dispatchOptions.find(
    (o) => String(o.dispatchIdent) === relatedDispatch
  );

  // Form A: selecting a show prefills position (from the dispatch row's
  // skill) and expected/actual pay (from the wage statement, §4.5) — both
  // stay member-editable.
  const selectShow = (value: string) => {
    setRelatedDispatch(value);
    if (!isA) return;
    const opt = dispatchOptions.find((o) => String(o.dispatchIdent) === value);
    if (!opt) return;
    if (!positionDirty && opt.skill) setPosition(opt.skill);
    // only fill fields that were empty when the show was picked — never
    // overwrite something the member typed
    const fillExpected = !expectedPay;
    const fillActual = !actualPay;
    if (!fillExpected && !fillActual) return;
    const req = new GetSupportWagePrefill();
    req.dispatchIdent = opt.dispatchIdent;
    apiServices.client
      .get(req)
      .then((r) => {
        if (!r.hasStatement) return;
        let applied = false;
        if (fillExpected && r.expectedPay != null) {
          setExpectedPay(String(r.expectedPay));
          applied = true;
        }
        if (fillActual && r.actualPay != null) {
          setActualPay(String(r.actualPay));
          applied = true;
        }
        if (applied)
          setPrefillNote(
            'Prefilled from your wage statement — edit if it doesn’t match.'
          );
      })
      .catch(() => {}); // prefill is best-effort
  };

  const validate = (): string | null => {
    const needsShow = isA || isC;
    const showChosen =
      (relatedDispatch !== '' && relatedDispatch !== OTHER) ||
      (relatedDispatch === OTHER && showFreeText.trim() !== '');
    if (needsShow && !showChosen)
      return isA
        ? 'Please pick the show — or choose "My show isn’t listed" and type it.'
        : 'Please pick the show — or choose "Another show" and type its name.';
    if (isA && !workDate) return 'Please enter the date you worked.';
    if (isA && !position.trim()) return 'Please enter the position you worked.';
    if (isB && urgent === '') return 'Please answer whether this is happening right now.';
    if ((isD || isE || isF || isG) && !subject.trim()) return 'Please enter a subject.';
    if (!body.trim())
      return isA
        ? 'Please describe what happened.'
        : isB
        ? 'Please describe what you are being asked to do.'
        : isC
        ? 'Please tell us what you need to know.'
        : 'Please enter the details.';
    for (const v of [expectedPay, actualPay, hoursWorked])
      if (v && parseFloat(v) < 0) return 'Pay and hours can’t be negative.';
    if (evidence) return attachmentPrecheck(evidence);
    return null;
  };

  // every free-text field this form sends, for the T7 pre-flight scan
  const composerFields = (): ComposerField[] =>
    [
      { key: 'subject', label: 'Subject', text: subject },
      { key: 'body', label: 'Details', text: body },
      { key: 'showFreeText', label: 'Show name', text: relatedDispatch === OTHER ? showFreeText : '' },
      { key: 'position', label: 'Position', text: position },
      { key: 'rateApplied', label: 'Rate applied', text: rateApplied },
      { key: 'otPenaltyMissed', label: 'Overtime / penalties', text: otPenaltyMissed },
      { key: 'contractUnderstanding', label: 'Contract understanding', text: contractUnderstanding },
    ].filter((f) => f.text.trim() !== '');

  const submit = async () => {
    if (sending) return;
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSending(true);
    // T7 inline reword: flag banned language before the ticket is attempted.
    // Advisory — the server re-scans inside submit either way.
    const hits = await checkLanguage(composerFields());
    setLangHits(hits);
    if (hits.length > 0) {
      setSending(false);
      return;
    }
    let ticketId: number | null = null;
    try {
      const req = new SubmitSupportTicket();
      req.category = meta.value;
      if (subject.trim()) req.subject = subject.trim();
      req.body = body.trim();
      if (relatedDispatch && relatedDispatch !== OTHER)
        req.relatedDispatchIdent = parseInt(relatedDispatch, 10);
      if (relatedDispatch === OTHER && showFreeText.trim())
        req.relatedShowFreeText = showFreeText.trim();
      if (meta.confidential) req.isConfidential = confidential;
      if (isB) req.isUrgent = urgent === 'yes';
      if (isA) {
        req.workDate = workDate;
        req.position = position.trim();
        if (expectedPay) req.expectedPay = parseFloat(expectedPay);
        if (actualPay) req.actualPay = parseFloat(actualPay);
        if (hoursWorked) req.hoursWorked = parseFloat(hoursWorked);
        if (rateApplied.trim()) req.rateApplied = rateApplied.trim();
        if (otPenaltyMissed.trim()) req.otPenaltyMissed = otPenaltyMissed.trim();
        if (callType) req.callType = callType;
      }
      if (isB) {
        if (contractUnderstanding.trim())
          req.contractUnderstanding = contractUnderstanding.trim();
        if (position.trim()) req.position = position.trim();
      }
      if (isC && callDate) req.callDate = callDate;
      const res = await apiServices.client.post(req);
      ticketId = res.ticketId;
    } catch (e: any) {
      setError(
        e?.responseStatus?.message || 'Something went wrong sending your request.'
      );
      setSending(false);
      return;
    }
    // The ticket exists from here on — an upload failure must not strand the
    // member on the composer, so route to the ticket and surface it there.
    let uploadError: string | null = null;
    if (evidence && meta.evidence) {
      try {
        await uploadTicketAttachment(ticketId!, evidence);
      } catch (e: any) {
        uploadError = e?.message || 'The file upload failed.';
      }
    }
    navigate(`/help/ticket/${ticketId}`, {
      state: uploadError ? { uploadError } : undefined,
    });
  };

  if (configLoading)
    return (
      <div className="mx-auto w-full max-w-2xl dark:text-white">
        <Spinner color="info" />
      </div>
    );

  // T7: ticket ban — the A-09 message replaces the form (composer only;
  // the rest of the portal is untouched)
  if (config?.ticketBanned)
    return (
      <ClosedPanel title={meta.title} message={config.bannedMessage} />
    );

  // D-01 vision-text mode: composer closed outside business hours
  if (config && !config.supportOpen)
    return (
      <ClosedPanel title={meta.title} message={config.closedMessage} />
    );

  // Form F: the good-standing gate comes BEFORE the form (D-07.3/.4)
  if (isF && config && !config.membershipFormOpen)
    return (
      <ClosedPanel title={meta.title} message={config.membershipFormMessage} />
    );

  return (
    <div className="mx-auto w-full flex max-w-2xl flex-col gap-6 dark:text-white">
      <div>
        <Link
          to="/help/new"
          className="text-sm text-indigo-600 dark:text-indigo-400"
        >
          &larr; All request types
        </Link>
        <h2 className="mt-1 text-2xl font-bold">{meta.title}</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {meta.destination}. We reply here in the portal — check My Requests
          for answers.
        </p>
      </div>

      {config && config.supportOpen && !config.withinBusinessHours && (
        <AfterHoursBanner />
      )}

      <div className="flex flex-col gap-4">
        {/* ── show / call picker (A, B, C, D, G) ── */}
        {usesShowDropdown && (
          <div>
            <label htmlFor="related-show" className={LABEL_CLS}>
              {isD
                ? 'Related show or employer (optional)'
                : isB
                ? 'Which show or call is this about?'
                : isG
                ? 'Related show (optional)'
                : 'Which show is this about?'}
            </label>
            <Select
              id="related-show"
              value={relatedDispatch}
              onChange={(e) => selectShow(e.target.value)}
            >
              <option value="">
                {isB
                  ? 'General question — not about a specific call'
                  : isD
                  ? 'Not about a specific show or employer'
                  : isG
                  ? 'Not about a specific call'
                  : 'Choose the show…'}
              </option>
              {dispatchOptions.map((o) => (
                <option key={o.dispatchIdent} value={o.dispatchIdent}>
                  {[o.show, o.site].filter(Boolean).join(' — ')}
                  {o.startingDate ? ` (${fmtDate(o.startingDate)})` : ''}
                </option>
              ))}
              {(isA || isC || isD) && (
                <option value={OTHER}>
                  {isA
                    ? 'My show isn’t listed — I’ll type it'
                    : isD
                    ? 'Somewhere else — I’ll type it'
                    : 'Another show — I’ll type it'}
                </option>
              )}
            </Select>
            {relatedDispatch === OTHER && (
              <div className="mt-2">
                <TextInput
                  id="show-free-text"
                  value={showFreeText}
                  maxLength={200}
                  placeholder={
                    isD ? 'Show or employer name' : 'Show name'
                  }
                  onChange={(e: any) => setShowFreeText(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Form A required fields ── */}
        {isA && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="work-date" className={LABEL_CLS}>
                  Date you worked
                </label>
                <input
                  id="work-date"
                  type="date"
                  className={INPUT_CLS}
                  value={workDate}
                  onChange={(e) => setWorkDate(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="position" className={LABEL_CLS}>
                  Position you worked
                </label>
                <TextInput
                  id="position"
                  value={position}
                  maxLength={100}
                  placeholder="e.g. Rigger, Spot Op"
                  onChange={(e: any) => {
                    setPosition(e.target.value);
                    setPositionDirty(true);
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* ── Form B urgency ── */}
        {isB && (
          <div>
            <span className={LABEL_CLS}>Is this happening right now?</span>
            <div className="flex gap-6">
              {(['yes', 'no'] as const).map((v) => (
                <label
                  key={v}
                  className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-300"
                >
                  <input
                    type="radio"
                    name="urgent"
                    value={v}
                    checked={urgent === v}
                    onChange={() => setUrgent(v)}
                    className="border-gray-300 dark:border-gray-600"
                  />
                  {v === 'yes' ? 'Yes — I’m on the call now' : 'No'}
                </label>
              ))}
            </div>
            {urgent === 'yes' && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                We’ll flag this as urgent so it’s seen first.
              </p>
            )}
          </div>
        )}

        {/* ── subject (D, E, F, G) ── */}
        {(isD || isE || isF || isG) && (
          <div>
            <label htmlFor="subject" className={LABEL_CLS}>
              Subject
            </label>
            <TextInput
              id="subject"
              value={subject}
              maxLength={200}
              placeholder={
                isG ? 'What is your question about?' : 'What is this about?'
              }
              onChange={(e: any) => setSubject(e.target.value)}
            />
          </div>
        )}

        {/* ── the long text every form requires ── */}
        <div>
          <label htmlFor="message" className={LABEL_CLS}>
            {isA
              ? 'What happened?'
              : isB
              ? 'What are you being asked to do?'
              : isC
              ? 'What do you need to know?'
              : isG
              ? 'Your question'
              : 'Details'}
          </label>
          <textarea
            id="message"
            rows={6}
            className={INPUT_CLS}
            placeholder={
              isA
                ? 'Tell us what went wrong with your pay…'
                : isB
                ? 'Describe the situation…'
                : isC
                ? 'Times, location, details…'
                : 'Tell us more…'
            }
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        {/* ── Form A optional details ── */}
        {isA && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-4">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-300">
              Pay details{' '}
              <span className="font-normal text-gray-500 dark:text-gray-400">
                (optional — fill in what you know)
              </span>
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="expected-pay" className={LABEL_CLS}>
                  Pay you expected ($)
                </label>
                <input
                  id="expected-pay"
                  type="number"
                  min="0"
                  step="0.01"
                  className={INPUT_CLS}
                  value={expectedPay}
                  onChange={(e) => setExpectedPay(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="actual-pay" className={LABEL_CLS}>
                  Pay you received ($)
                </label>
                <input
                  id="actual-pay"
                  type="number"
                  min="0"
                  step="0.01"
                  className={INPUT_CLS}
                  value={actualPay}
                  onChange={(e) => setActualPay(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="hours-worked" className={LABEL_CLS}>
                  Hours worked
                </label>
                <input
                  id="hours-worked"
                  type="number"
                  min="0"
                  step="0.25"
                  className={INPUT_CLS}
                  value={hoursWorked}
                  onChange={(e) => setHoursWorked(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="rate-applied" className={LABEL_CLS}>
                  Rate that was applied
                </label>
                <TextInput
                  id="rate-applied"
                  value={rateApplied}
                  maxLength={100}
                  placeholder="e.g. $42.50/hr"
                  onChange={(e: any) => setRateApplied(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="call-type" className={LABEL_CLS}>
                  Type of call
                </label>
                <Select
                  id="call-type"
                  value={callType}
                  onChange={(e) => setCallType(e.target.value)}
                >
                  <option value="">Not sure / other</option>
                  <option value="Load-in">Load-in</option>
                  <option value="Show">Show</option>
                  <option value="Load-out">Load-out</option>
                  <option value="Other">Other</option>
                </Select>
              </div>
            </div>
            {prefillNote && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {prefillNote}
              </p>
            )}
            <div>
              <label htmlFor="ot-penalty" className={LABEL_CLS}>
                Overtime or penalties you think were missed
              </label>
              <textarea
                id="ot-penalty"
                rows={3}
                className={INPUT_CLS}
                placeholder="e.g. meal penalty, OT after 8…"
                value={otPenaltyMissed}
                onChange={(e) => setOtPenaltyMissed(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* ── Form B optional fields ── */}
        {isB && (
          <>
            <div>
              <label htmlFor="contract-understanding" className={LABEL_CLS}>
                What do you think the contract says? (optional)
              </label>
              <textarea
                id="contract-understanding"
                rows={3}
                className={INPUT_CLS}
                placeholder="Your understanding, in your own words…"
                value={contractUnderstanding}
                onChange={(e) => setContractUnderstanding(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="position" className={LABEL_CLS}>
                Position you’re working (optional)
              </label>
              <TextInput
                id="position"
                value={position}
                maxLength={100}
                placeholder="e.g. Rigger, Spot Op"
                onChange={(e: any) => setPosition(e.target.value)}
              />
            </div>
          </>
        )}

        {/* ── Form C call date ── */}
        {isC && (
          <div>
            <label htmlFor="call-date" className={LABEL_CLS}>
              Date of the call (optional)
            </label>
            <input
              id="call-date"
              type="date"
              className={INPUT_CLS}
              value={callDate}
              onChange={(e) => setCallDate(e.target.value)}
            />
          </div>
        )}

        {/* ── confidential toggle (D, E) ── */}
        {meta.confidential && (
          <label className="flex items-start gap-2 text-sm text-gray-900 dark:text-gray-300">
            <input
              type="checkbox"
              checked={confidential}
              onChange={(e) => setConfidential(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
            />
            <span>
              Submit confidentially
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Only the {isD ? 'organizer' : 'political director'} will be
                able to see this request — not the front desk or anyone else.
              </span>
            </span>
          </label>
        )}

        {/* ── evidence (A, B, F, G) ── */}
        {meta.evidence && (
          <div>
            <label htmlFor="evidence" className={LABEL_CLS}>
              Attach a file (optional)
            </label>
            <input
              id="evidence"
              type="file"
              accept={ATTACHMENT_ACCEPT}
              onChange={(e) => setEvidence(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-900 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 dark:file:bg-gray-700 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 dark:file:text-gray-200"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {evidence
                ? `${evidence.name} (${fmtBytes(evidence.size)})`
                : isA
                ? 'A pay stub, photo, or document that helps — pdf, jpg, png, heic, xlsx, or csv, up to 15 MB.'
                : 'A photo or document that helps explain — pdf, jpg, png, heic, xlsx, or csv, up to 15 MB.'}
            </p>
          </div>
        )}

        <MisuseDisclaimer />

        <LanguageWarning fields={composerFields()} hits={langHits} />

        {error && (
          <Alert color="failure" icon={HiInformationCircle}>
            {error}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={submit} disabled={sending}>
            {sending ? <Spinner size="sm" /> : 'Send Request'}
          </Button>
          <Button color="gray" onClick={() => navigate('/help/new')}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

// Business-hours closed / membership-gate closed — same shell, different
// message. The server enforces both; this is just the friendly face.
const ClosedPanel: React.FC<{ title: string; message?: string }> = ({
  title,
  message,
}) => {
  const navigate = useNavigate();
  return (
    <div className="mx-auto w-full flex max-w-2xl flex-col gap-6 dark:text-white">
      <div>
        <Link
          to="/help/new"
          className="text-sm text-indigo-600 dark:text-indigo-400"
        >
          &larr; All request types
        </Link>
        <h2 className="mt-1 text-2xl font-bold">{title}</h2>
      </div>
      <Alert color="warning" icon={HiInformationCircle}>
        {message || 'This form isn’t available right now.'}
      </Alert>
      <div>
        <Button color="gray" onClick={() => navigate('/help')}>
          Back to My Requests
        </Button>
      </div>
    </div>
  );
};

export default SupportNewTicketPage;
